import type { GenerationQueueItemResponse, GenerationQueueStateResponse } from '@malaberg/shared';
import { create } from 'zustand';

export interface GenerationQueueStore {
  state: GenerationQueueStateResponse | null;
  items: GenerationQueueItemResponse[];
  loading: boolean;
  mutating: boolean;
  error: string | null;

  setQueue: (state: GenerationQueueStateResponse, items: GenerationQueueItemResponse[]) => void;
  setState: (state: GenerationQueueStateResponse) => void;
  mergeItem: (state: GenerationQueueStateResponse, item: GenerationQueueItemResponse) => void;
  mergeItems: (state: GenerationQueueStateResponse, items: GenerationQueueItemResponse[]) => void;
  setLoading: (loading: boolean) => void;
  setMutating: (mutating: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  state: null,
  items: [],
  loading: false,
  mutating: false,
  error: null,
};

function sortQueueItems(items: GenerationQueueItemResponse[]): GenerationQueueItemResponse[] {
  return [...items].sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position;
    return a.createdAt.localeCompare(b.createdAt);
  });
}

function upsertItems(
  existing: GenerationQueueItemResponse[],
  incoming: GenerationQueueItemResponse[]
): GenerationQueueItemResponse[] {
  const byId = new Map(existing.map((item) => [item.id, item]));
  for (const item of incoming) {
    byId.set(item.id, item);
  }
  return sortQueueItems(Array.from(byId.values()));
}

export const useGenerationQueueStore = create<GenerationQueueStore>((set) => ({
  ...initialState,

  setQueue(state, items) {
    set({ state, items: sortQueueItems(items), error: null });
  },

  setState(state) {
    set({ state, error: null });
  },

  mergeItem(state, item) {
    set((prev) => ({
      state,
      items: upsertItems(prev.items, [item]),
      error: null,
    }));
  },

  mergeItems(state, items) {
    set((prev) => ({
      state,
      items: upsertItems(prev.items, items),
      error: null,
    }));
  },

  setLoading(loading) {
    set({ loading });
  },

  setMutating(mutating) {
    set({ mutating });
  },

  setError(error) {
    set({ error });
  },

  reset() {
    set(initialState);
  },
}));
